import React, { useCallback } from 'react';
import { endOfDay, isAfter, startOfDay } from 'date-fns';

import useToggle from '@lyearn/core/hooks/useToggle';
import { formatRange, RangeFormat } from '@lyearn/core/utils/date/formatRange';
import { useTranslation } from '@lyearn/i18n';
import { AlertTriangle, CalendarOutline as IconCalendarOutline } from '@lyearn/icons';
import { Button, DatePickerDate, useSnackbar } from '@lyearn/ui';
import DateSelector from '@/components/DateFilter/DateSelector';

import { DateFilterListItem, DateFilterListItemKey, DateFilterListItems } from './constants';
import { buttonStyles } from './styles';
import { DateFilterPosition, DateFilterProps } from './types';

function DateFilter(props: DateFilterProps) {
  const {
    isOpen,
    onToggleFilter,
    dateRange,
    selectedDateFilterItem,
    childRenderer,
    position = DateFilterPosition.Right,
    options,
  } = props;
  const { t } = useTranslation();
  const [open, { toggle }] = useToggle(isOpen ?? false);
  const { showSnackbar } = useSnackbar();
  const [value, setValue] = React.useState<string>(t('Common:AllTime'));
  const calendarButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const onDone = (
    startTime: DatePickerDate,
    endTime: DatePickerDate,
    listItem?: DateFilterListItem,
  ) => {
    if (!startTime || !endTime || !listItem) {
      props.onDone();
      toggle();

      return;
    }
    const startTimeStamp = startOfDay(startTime);
    const endTimeStamp = endOfDay(endTime);
    if (isAfter(endTimeStamp, startTimeStamp)) {
      listItem.key === DateFilterListItemKey.Custom
        ? setValue(formatRange(startTime, endTime, RangeFormat.Date, { includeYear: true }))
        : setValue(t(listItem.label));
      toggle();
      props.onDone(startTimeStamp, endTimeStamp, listItem);
    } else
      showSnackbar({
        startIcon: <AlertTriangle fillColorToken="icon-error-light" />,
        message: t('Common:error.RangeError'),
      });
  };

  const onCancel = useCallback(() => {
    toggle(!open); // not working without open
    onToggleFilter?.();
  }, [onToggleFilter, open, toggle]);

  return (
    <DateSelector
      key={open.toString()} // key added to avoid sync between state and props
      endTime={dateRange?.endTime as Date}
      open={open}
      options={options || DateFilterListItems}
      position={position}
      selectedOption={selectedDateFilterItem}
      startTime={dateRange?.startTime as Date}
      onCancel={onCancel}
      onDone={onDone}>
      {childRenderer ? (
        childRenderer(open, toggle, calendarButtonRef)
      ) : (
        <Button
          ref={calendarButtonRef}
          appearance="secondary"
          className={buttonStyles}
          endIcon={<IconCalendarOutline className="fill-current text-icon-primary ms-8" />}
          variant="outlined"
          onClick={toggle}>
          {value}
        </Button>
      )}
    </DateSelector>
  );
}

export default DateFilter;
